import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { Trash2, Copy, Move, Image, Type, Building, MapPin, Phone, Mail, Globe } from 'lucide-react';
import WYSIWYGEditor, { WYSIWYGEditorRef } from './forms/WYSIWYGEditor';
import Modal from './ui/Modal';

// Block types for default first page
interface DefaultFirstPageBlock {
  id: string;
  type: string;
  properties: Record<string, any>;
  content?: string;
}

interface DefaultFirstPageEditorProps {
  initialContent?: string;
  onSave: (content: string) => void;
  companyData?: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    email?: string;
    website?: string;
    logoUrl?: string;
  };
}

const DefaultFirstPageEditor: React.FC<DefaultFirstPageEditorProps> = ({
  initialContent = '',
  onSave,
  companyData
}) => {
  const [blocks, setBlocks] = useState<DefaultFirstPageBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [draggedBlockType, setDraggedBlockType] = useState<string | null>(null);
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const wysiwygEditorRef = useRef<WYSIWYGEditorRef>(null);

  // Parse initial content when component loads
  useEffect(() => {
    if (initialContent) {
      try {
        const parsedBlocks = JSON.parse(initialContent);
        if (Array.isArray(parsedBlocks)) {
          setBlocks(parsedBlocks);
        }
      } catch (error) {
        console.error('Failed to parse initial content:', error);
        // If parsing fails, treat as empty
        setBlocks([]);
      }
    }
  }, [initialContent]);

  // Available block types
  const availableBlocks = useMemo(() => [
    { 
      type: 'text', 
      label: 'Text Block', 
      icon: Type, 
      description: 'Add text content (supports pasting)',
      defaultProperties: { fontSize: 'medium', fontWeight: 'normal', textAlign: 'left', color: '#000000' },
      defaultContent: ''
    },
    { 
      type: 'logo', 
      label: 'Company Logo', 
      icon: Image, 
      description: 'Insert company logo',
      defaultProperties: { width: 200, height: 100 },
      defaultContent: ''
    },
    { 
      type: 'logoAddress', 
      label: 'Logo + Contact Info', 
      icon: Building, 
      description: 'Logo with address, phone, and email',
      defaultProperties: { logoWidth: 100, logoHeight: 100, fontSize: 'medium', fontWeight: 'normal' },
      defaultContent: '{{CompanyAddress}}\n{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}\n📞 {{CompanyPhone}}\n✉️ {{CompanyEmail}}'
    },
    { 
      type: 'companyName', 
      label: 'Company Name', 
      icon: Building, 
      description: 'Company name',
      defaultProperties: { fontSize: 'large', fontWeight: 'bold', textAlign: 'left', color: '#000000' },
      defaultContent: '{{CompanyName}}'
    },
    { 
      type: 'companyAddress', 
      label: 'Company Address', 
      icon: MapPin, 
      description: 'Full company address',
      defaultProperties: { fontSize: 'medium', fontWeight: 'normal', textAlign: 'left', color: '#000000' },
      defaultContent: '{{CompanyAddress}}\n{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}'
    },
    { 
      type: 'companyPhone', 
      label: 'Company Phone', 
      icon: Phone, 
      description: 'Company phone number',
      defaultProperties: { fontSize: 'medium', fontWeight: 'normal', textAlign: 'left', color: '#000000' },
      defaultContent: '📞 {{CompanyPhone}}'
    },
    { 
      type: 'companyEmail', 
      label: 'Company Email', 
      icon: Mail, 
      description: 'Company email address',
      defaultProperties: { fontSize: 'medium', fontWeight: 'normal', textAlign: 'left', color: '#000000' },
      defaultContent: '✉️ {{CompanyEmail}}'
    },
    { 
      type: 'companyWebsite', 
      label: 'Company Website', 
      icon: Globe, 
      description: 'Company website',
      defaultProperties: { fontSize: 'medium', fontWeight: 'normal', textAlign: 'left', color: '#000000' },
      defaultContent: '🌐 {{CompanyWebsite}}'
    },
  ], []);

  // Add a new block
  const addBlock = useCallback((type: string, position?: number) => {
    const blockTypeConfig = availableBlocks.find(bt => bt.type === type);
    const newBlock: DefaultFirstPageBlock = {
      id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      properties: blockTypeConfig?.defaultProperties || getDefaultProperties(type),
      content: blockTypeConfig?.defaultContent || getDefaultContent(type)
    };

    setBlocks(prev => {
      const newBlocks = [...prev];
      if (position !== undefined) {
        newBlocks.splice(position, 0, newBlock);
      } else {
        newBlocks.push(newBlock);
      }
      // Auto-save when block is added
      onSave(JSON.stringify(newBlocks));
      return newBlocks;
    });

    setSelectedBlockId(newBlock.id);
  }, [onSave, availableBlocks]);

  // Get default properties for a block type
  const getDefaultProperties = (type: string): Record<string, any> => {
    switch (type) {
      case 'text':
        return {
          fontSize: 'medium',
          fontWeight: 'normal',
          textAlign: 'left',
          color: '#000000'
        };
      case 'logo':
        return {
          width: 200,
          height: 100,
          align: 'center'
        };
      case 'logoAddress':
        return {
          logoWidth: 100,
          logoHeight: 100,
          fontSize: 'medium',
          fontWeight: 'normal'
        };
      case 'companyName':
        return {
          fontSize: 'large',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      case 'companyAddress':
      case 'companyPhone':
      case 'companyEmail':
      case 'companyWebsite':
        return {
          fontSize: 'medium',
          fontWeight: 'normal',
          textAlign: 'left'
        };
      default:
        return {};
    }
  };

  // Get default content for a block type
  const getDefaultContent = (type: string): string => {
    switch (type) {
      case 'text':
        return 'Enter your text here...';
      case 'logo':
        return '{{CompanyLogo}}';
      case 'logoAddress':
        return '{{CompanyAddress}}\n{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}\n📞 {{CompanyPhone}}\n✉️ {{CompanyEmail}}';
      case 'companyName':
        return '{{CompanyName}}';
      case 'companyAddress':
        return '{{CompanyAddress}}\n{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}';
      case 'companyPhone':
        return '📞 {{CompanyPhone}}';
      case 'companyEmail':
        return '✉️ {{CompanyEmail}}';
      case 'companyWebsite':
        return '🌐 {{CompanyWebsite}}';
      default:
        return '';
    }
  };

  // Update block properties
  const updateBlockProperties = useCallback((blockId: string, properties: Record<string, any>) => {
    setBlocks(prev => {
      const updatedBlocks = prev.map(block => 
        block.id === blockId 
          ? { ...block, properties: { ...block.properties, ...properties } }
          : block
      );
      
      // Auto-save when properties change
      onSave(JSON.stringify(updatedBlocks));
      
      return updatedBlocks;
    });
  }, [onSave]);

  // Update block content
  const updateBlockContent = useCallback((blockId: string, content: string) => {
    setBlocks(prev => {
      const updatedBlocks = prev.map(block => 
        block.id === blockId 
          ? { ...block, content }
          : block
      );
      // Auto-save when content changes
      onSave(JSON.stringify(updatedBlocks));
      return updatedBlocks;
    });
  }, [onSave]);

  // Delete block
  const deleteBlock = useCallback((blockId: string) => {
    setBlocks(prev => {
      const updatedBlocks = prev.filter(block => block.id !== blockId);
      // Auto-save when block is deleted
      onSave(JSON.stringify(updatedBlocks));
      return updatedBlocks;
    });
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }, [selectedBlockId, onSave]);

  // Duplicate block
  const duplicateBlock = useCallback((blockId: string) => {
    const blockToDuplicate = blocks.find(block => block.id === blockId);
    if (blockToDuplicate) {
      const newBlock: DefaultFirstPageBlock = {
        ...blockToDuplicate,
        id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
      setBlocks(prev => {
        const updatedBlocks = [...prev, newBlock];
        // Auto-save when block is duplicated
        onSave(JSON.stringify(updatedBlocks));
        return updatedBlocks;
      });
    }
  }, [blocks, onSave]);

  // Move block
  const moveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    setBlocks(prev => {
      const newBlocks = [...prev];
      const index = newBlocks.findIndex(block => block.id === blockId);
      
      if (direction === 'up' && index > 0) {
        [newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]];
      } else if (direction === 'down' && index < newBlocks.length - 1) {
        [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
      }
      
      // Auto-save when block is moved
      onSave(JSON.stringify(newBlocks));
      return newBlocks;
    });
  }, [onSave]);

  // Handle block drag over
  const handleBlockDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set drag over index if we're actually dragging something
    if (draggedBlockId || draggedBlockType) {
      setDragOverIndex(targetIndex);
    }
  };

  // Handle block drop
  const handleBlockDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Block drop:', { draggedBlockId, draggedBlockType, targetIndex });
    
    if (draggedBlockId) {
      // Reorder existing block
      const draggedBlock = blocks.find(block => block.id === draggedBlockId);
      if (draggedBlock) {
        const currentIndex = blocks.findIndex(block => block.id === draggedBlockId);
        console.log('Reordering block:', { currentIndex, targetIndex });
        
        if (currentIndex !== targetIndex) {
          const newBlocks = [...blocks];
          const [removed] = newBlocks.splice(currentIndex, 1);
          newBlocks.splice(targetIndex, 0, removed);
          
          setBlocks(newBlocks);
          // Auto-save when block is reordered
          onSave(JSON.stringify(newBlocks));
        }
      }
    } else if (draggedBlockType) {
      // Add new block at specific position
      const blockTypeConfig = availableBlocks.find(bt => bt.type === draggedBlockType);
      if (blockTypeConfig) {
        const newBlock: DefaultFirstPageBlock = {
          id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: draggedBlockType,
          properties: { ...blockTypeConfig.defaultProperties },
          content: blockTypeConfig.defaultContent
        };

        const newBlocks = [...blocks];
        newBlocks.splice(targetIndex, 0, newBlock);
        
        setBlocks(newBlocks);
        // Auto-save when new block is added
        onSave(JSON.stringify(newBlocks));
      }
    }
    
    setDraggedBlockId(null);
    setDraggedBlockType(null);
    setDragOverIndex(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedBlockId(null);
    setDraggedBlockType(null);
    setDragOverIndex(null);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  // Open text editor
  const openTextEditor = useCallback((blockId: string) => {
    setEditingBlockId(blockId);
    setShowTextEditor(true);
  }, []);

  // Close text editor and save content
  const closeTextEditor = useCallback(() => {
    if (editingBlockId && wysiwygEditorRef.current) {
      const htmlContent = wysiwygEditorRef.current.getContent();
      // Store HTML content directly for rich text formatting
      updateBlockContent(editingBlockId, htmlContent);
    }
    setShowTextEditor(false);
    setEditingBlockId(null);
  }, [editingBlockId, updateBlockContent]);

  // Handle drag start for new blocks
  const handleDragStart = (e: React.DragEvent, blockType: string) => {
    setDraggedBlockType(blockType);
    setDraggedBlockId(null);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drag start for existing blocks
  const handleBlockDragStart = (e: React.DragEvent, blockId: string) => {
    setDraggedBlockId(blockId);
    setDraggedBlockType(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', blockId);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedBlockType) {
      addBlock(draggedBlockType);
      setDraggedBlockType(null);
    }
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Render block content
  const renderBlockContent = (block: DefaultFirstPageBlock) => {
    const { type, content, properties } = block;

    switch (type) {
      case 'text':
        return (
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Text Content</span>
              <button
                onClick={() => openTextEditor(block.id)}
                className="px-3 py-1 text-xs bg-accent-color text-white rounded hover:bg-accent-color-600"
              >
                Edit with WYSIWYG
              </button>
            </div>
            <div className="w-full p-3 border border-gray-300 rounded bg-white min-h-[100px]"
              style={{
                fontSize: properties.fontSize === 'small' ? '14px' : 
                         properties.fontSize === 'medium' ? '16px' : 
                         properties.fontSize === 'large' ? '18px' : '20px',
                fontWeight: properties.fontWeight === 'bold' ? 'bold' : 'normal',
                textAlign: properties.textAlign,
                color: properties.color
              }}
            >
              {content ? (
                <div className="text-black" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
              ) : (
                <div className="text-gray-500 italic">Click "Edit with WYSIWYG" to add content...</div>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              💡 Click "Edit with WYSIWYG" for rich text editing with formatting, paragraphs, and alignment
            </div>
          </div>
        );
      case 'logo':
        return (
          <div className="text-center">
            <div
              style={{
                width: `${properties.width}px`,
                height: `${properties.height}px`,
                display: 'inline-block',
                backgroundImage: `url(${companyData?.logoUrl || '{{CompanyLogo}}'})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
              title="Company Logo"
            />
          </div>
        );
      case 'logoAddress':
        return (
          <div className="flex items-start space-x-4 bg-white p-3 border border-gray-200 rounded">
            {/* Logo */}
            <div
              style={{
                width: `${properties.logoWidth}px`,
                height: `${properties.logoHeight}px`,
                backgroundImage: `url(${companyData?.logoUrl || '{{CompanyLogo}}'})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                flexShrink: 0
              }}
              title="Company Logo"
            />
            {/* Address */}
            <div className="flex-1">
              <div
                style={{
                  fontSize: properties.fontSize === 'small' ? '14px' : 
                           properties.fontSize === 'medium' ? '16px' : 
                           properties.fontSize === 'large' ? '18px' : '20px',
                  fontWeight: properties.fontWeight === 'bold' ? 'bold' : 'normal',
                  lineHeight: '1.4'
                }}
              >
                <pre className="whitespace-pre-wrap font-sans text-black">{content}</pre>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="p-2 border border-gray-200 rounded bg-white">
            <pre className="whitespace-pre-wrap font-sans text-black">{content}</pre>
          </div>
        );
    }
  };

  // Render properties panel
  const renderPropertiesPanel = () => {
    const selectedBlock = blocks.find(block => block.id === selectedBlockId);
    if (!selectedBlock) return null;

    const { type, properties } = selectedBlock;

    return (
      <div className="w-80 bg-white border-l border-gray-300 p-4 overflow-y-auto">
        <h3 className="text-lg font-medium mb-4">Block Properties</h3>
        
        <div className="space-y-4">
          {/* Text properties */}
          {(type === 'text' || type === 'companyName' || type === 'companyAddress' || 
            type === 'companyPhone' || type === 'companyEmail' || type === 'companyWebsite') && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
                <select
                  value={properties.fontSize}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { fontSize: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="extra-large">Extra Large</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
                <select
                  value={properties.fontWeight}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { fontWeight: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Align</label>
                <select
                  value={properties.textAlign}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { textAlign: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              
              {type === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                  <input
                    type="color"
                    value={properties.color}
                    onChange={(e) => updateBlockProperties(selectedBlockId, { color: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded cursor-pointer"
                  />
                </div>
              )}
            </>
          )}

          {/* Logo properties */}
          {type === 'logo' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width (px)</label>
                <input
                  type="number"
                  value={properties.width}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { width: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="50"
                  max="800"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height (px)</label>
                <input
                  type="number"
                  value={properties.height}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { height: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="50"
                  max="600"
                />
              </div>
            </>
          )}

          {/* Logo + Address properties */}
          {type === 'logoAddress' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Width (px)</label>
                <input
                  type="number"
                  value={properties.logoWidth}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { logoWidth: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="50"
                  max="300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Height (px)</label>
                <input
                  type="number"
                  value={properties.logoHeight}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { logoHeight: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="50"
                  max="300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
                <select
                  value={properties.fontSize}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { fontSize: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="extra-large">Extra Large</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
                <select
                  value={properties.fontWeight}
                  onChange={(e) => updateBlockProperties(selectedBlockId, { fontWeight: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full">
      {/* Left Panel - Available Blocks */}
      <div className="w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-300 dark:border-gray-700 p-4 overflow-y-auto">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Available Blocks</h3>
        <div className="space-y-2">
          {availableBlocks.map((block) => {
            const Icon = block.icon;
            return (
              <div
                key={block.type}
                draggable
                onDragStart={(e) => handleDragStart(e, block.type)}
                className="p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded cursor-move hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <div className="flex items-center space-x-2">
                  <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{block.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{block.description}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Center Panel - Canvas (match TemplateBuilder look) */}
      <div className="flex-1 flex flex-col p-4">
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex-1 flex flex-col p-4 min-h-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Template Canvas</h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">Drag blocks from the left panel or click to add</div>
          </div>

          <div
            className="flex-1 overflow-y-auto space-y-2 min-h-0"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragLeave={handleDragLeave}
          >
            {blocks.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <p className="text-lg mb-2">No blocks added yet</p>
                <p className="text-sm">Drag blocks from the left panel to start building</p>
              </div>
            ) : (
              <div className="space-y-2 max-w-4xl mx-auto">
              {blocks.map((block, index) => (
                <React.Fragment key={block.id}>
                  {/* Drop zone above each block */}
                  <div
                    className={`h-4 transition-colors border-2 border-dashed rounded ${
                      dragOverIndex === index 
                        ? 'bg-accent-color-200 border-accent-color-400' 
                        : 'bg-transparent border-transparent hover:border-gray-300'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBlockDragOver(e, index);
                    }}
                    onDrop={(e) => handleBlockDrop(e, index)}
                  />
                  
                  <div
                    className={`bg-white dark:bg-gray-800 border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedBlockId === block.id 
                        ? 'border-accent-color bg-accent-color-50 dark:bg-accent-color-900/20' 
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                    onClick={() => setSelectedBlockId(prev => (prev === block.id ? null : block.id))}
                    draggable={true}
                    onDragStart={(e) => {
                      console.log('Block drag start:', block.id);
                      handleBlockDragStart(e, block.id);
                    }}
                    onDragEnd={() => {
                      console.log('Block drag end:', block.id);
                    }}
                  >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        {availableBlocks.find(b => b.type === block.type)?.label}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">#{index + 1}</span>
                    </div>
                    
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveBlock(block.id, 'up');
                        }}
                        disabled={index === 0}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                      >
                        <Move className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateBlock(block.id);
                        }}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBlock(block.id);
                        }}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="block-content">
                    {renderBlockContent(block)}
                  </div>
                  </div>
                </React.Fragment>
              ))}
              
              {/* Drop zone at the end */}
              {blocks.length > 0 && (
                <div
                  className={`h-4 transition-colors border-2 border-dashed rounded ${
                    dragOverIndex === blocks.length 
                      ? 'bg-accent-color-200 border-accent-color-400' 
                      : 'bg-transparent border-transparent hover:border-gray-300'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleBlockDragOver(e, blocks.length);
                  }}
                  onDrop={(e) => handleBlockDrop(e, blocks.length)}
                />
              )}
              </div>
            )}
            </div>
          </div>
      </div>

      {/* Right Panel - Properties */}
      {renderPropertiesPanel()}

      {/* WYSIWYG Editor Dialog */}
      <Modal
        isOpen={showTextEditor}
        onClose={() => {
          setShowTextEditor(false);
          setEditingBlockId(null);
        }}
        title="Edit Text Content"
        maxWidth="7xl"
      >
        <div className="flex flex-1 overflow-hidden h-[80vh]">
              {/* Left Panel - Placeholders */}
              <div className="w-80 bg-gray-50 border-r border-gray-300 p-4 overflow-y-auto">
                <h4 className="text-md font-medium mb-4">Insert Placeholders</h4>
                
                {/* Company Information */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Company Information</h5>
                  <div className="space-y-1">
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyName}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyName}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyAddress}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyAddress}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyCity}}, {{CompanyState}} {{CompanyZip}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyPhone}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyPhone}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyEmail}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyEmail}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{CompanyWebsite}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{CompanyWebsite}}'}
                    </button>
                  </div>
                </div>

                {/* Work Order Information */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Work Order Information</h5>
                  <div className="space-y-1">
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderNumber}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderNumber}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderPriority}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderPriority}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderStatus}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderStatus}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderCategory}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderCategory}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderDescription}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderDescription}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderCreatedDate}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderCreatedDate}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderDueDate}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderDueDate}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{WorkOrderNTE}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{WorkOrderNTE}}'}
                    </button>
                  </div>
                </div>

                {/* Client Information */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Client Information</h5>
                  <div className="space-y-1">
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{ClientName}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{ClientName}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{ClientAddress}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{ClientAddress}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{ClientCity}}, {{ClientState}} {{ClientZip}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{ClientCity}}, {{ClientState}} {{ClientZip}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{ClientPhone}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{ClientPhone}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{ClientEmail}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{ClientEmail}}'}
                    </button>
                  </div>
                </div>

                {/* User Information */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">User Information</h5>
                  <div className="space-y-1">
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{AssignedUserName}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{AssignedUserName}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{AssignedUserEmail}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{AssignedUserEmail}}'}
                    </button>
                    <button
                      onClick={() => wysiwygEditorRef.current?.insertHTML('{{AssignedUserPhone}}')}
                      className="w-full text-left px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100"
                    >
                      {'{{AssignedUserPhone}}'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Panel - WYSIWYG Editor */}
              <div className="flex-1 p-4 overflow-y-auto">
                <div className="h-full">
                  <div className="h-full flex flex-col">
                    <WYSIWYGEditor
                      ref={wysiwygEditorRef}
                      value={editingBlockId ? blocks.find(b => b.id === editingBlockId)?.content || '' : ''}
                      onChange={() => {}} // We'll get content on save
                      placeholder="Enter your text content here..."
                      companyLogoUrl={companyData?.logoUrl}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
              <button
                onClick={closeTextEditor}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Save & Close
              </button>
              <button
                onClick={() => {
                  setShowTextEditor(false);
                  setEditingBlockId(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
      </Modal>

    </div>
  );
};

export default DefaultFirstPageEditor;
